import React from "react";

import {
  Baby,
  Bed,
  Car,
  CreditCard,
  Dog,
  Gift,
  Home,
  IdCard,
  Laptop,
  Package,
  Pill,
  Receipt,
  School,
  Shield,
  ShieldAlert,
  Shirt,
  ShowerHead,
  Smartphone,
  Sofa,
  Users,
  UtensilsCrossed,
} from "lucide-react";

interface NeedIconProps {
  type: string;
  className?: string;
  /**
   * Title for the icon (for screen readers)
   */
  title?: string;
  /**
   * Whether the icon is decorative (hidden from screen readers)
   */
  isDecorative?: boolean;
  /**
   * Optional ID for the icon
   */
  id?: string;
}

// Maps explicit tag-type keys to icon components
const TYPE_TO_ICON = {
  adult: Users,
  baby: Baby,
  bed: Bed,
  bills: Receipt,
  car: Car,
  child: School,
  clean: ShowerHead,
  cleaning: ShowerHead,
  clothing: Shirt,
  documents: IdCard,
  education: School,
  family: Users,
  food: UtensilsCrossed,
  furniture: Sofa,
  gas: Car,
  general: Package,
  gift: Gift,
  groceries: UtensilsCrossed,
  health: Pill,
  home: Home,
  housing: Home,
  hygiene: ShowerHead,
  id: IdCard,
  infant: Baby,
  kids: School,
  laptop: Laptop,
  license: IdCard,
  medical: Pill,
  payment: CreditCard,
  pet: Dog,
  pets: Dog,
  phone: Smartphone,
  pill: Pill,
  rent: Home,
  ride: Car,
  safety: Shield,
  security: ShieldAlert,
  shirt: Shirt,
  shower: ShowerHead,
  smartphone: Smartphone,
  sofa: Sofa,
  tech: Laptop,
  toiletries: ShowerHead,
  transport: Car,
  truck: Car,
  utilities: Receipt,
  voucher: Gift,
} as const;

type IconKey = keyof typeof TYPE_TO_ICON;

// Ordered heuristic rules: [keywords, icon, title]
type HeuristicRule = [
  string[],
  React.ComponentType<{
    className?: string;
    "aria-hidden"?: boolean | "true" | "false";
    "aria-label"?: string;
    focusable?: boolean;
    id?: string;
    role?: string;
  }>,
  string,
];

const HEURISTIC_RULES: HeuristicRule[] = [
  [["baby", "infant"], Baby, "Baby/infant need"],
  [["child", "kid", "school", "education"], School, "Child/education need"],
  [["family", "adult"], Users, "Family/adult need"],
  [
    ["clothing", "shirt", "coat", "jacket", "shoe", "sock"],
    Shirt,
    "Clothing need",
  ],
  [["food", "grocer", "meal"], UtensilsCrossed, "Food/groceries need"],
  [["housing", "home", "rent", "shelter"], Home, "Housing/shelter need"],
  [
    ["health", "medical", "pill", "rx", "prescription"],
    Pill,
    "Health/medical need",
  ],
  [["transport", "ride", "gas", "car", "truck"], Car, "Transportation need"],
  [
    ["hygiene", "toiletr", "shower", "soap"],
    ShowerHead,
    "Hygiene/toiletries need",
  ],
  [["furniture", "sofa", "bed", "mattress"], Sofa, "Furniture/household need"],
  [
    ["tech", "laptop", "tablet", "phone"],
    Laptop,
    "Technology/electronics need",
  ],
  [["id", "license", "document", "paperwork"], IdCard, "Documents/ID need"],
  [["utilit", "bill", "payment"], Receipt, "Utilities/bills assistance"],
  [["safety", "security"], Shield, "Safety/security need"],
  [["pet", "dog", "cat"], Dog, "Pet-related need"],
  [["gift", "voucher"], Gift, "Gift/voucher assistance"],
  [["clean", "broom", "supplies"], ShowerHead, "Cleaning supplies need"],
];

function resolveIconAndTitle(tagType: string): {
  IconComponent: React.ComponentType<{
    className?: string;
    "aria-hidden"?: boolean | "true" | "false";
    "aria-label"?: string;
    focusable?: boolean;
    id?: string;
    role?: string;
  }>;
  iconTitle: string;
} {
  const directKey = (tagType.split(/[^a-z]+/).find((k) => k in TYPE_TO_ICON) ??
    "") as IconKey;

  if (directKey) {
    return {
      IconComponent: TYPE_TO_ICON[directKey],
      iconTitle: "General need",
    };
  }

  for (const [keywords, Icon, title] of HEURISTIC_RULES) {
    if (keywords.some((kw) => tagType.includes(kw))) {
      return { IconComponent: Icon, iconTitle: title };
    }
  }

  return { IconComponent: Package, iconTitle: "General need" };
}

function buildIconAriaProps(
  isDecorative: boolean,
  resolvedTitle: string,
): {
  "aria-hidden": boolean;
  "aria-label": string | undefined;
  role: string;
} {
  return {
    "aria-hidden": isDecorative,
    "aria-label": isDecorative ? undefined : resolvedTitle,
    role: isDecorative ? "presentation" : "img",
  };
}

/**
 * Default icon for a need based on tag type with accessibility support
 *
 * Accessibility features:
 * - Uses aria-label and role attributes for SR support
 * - Can be made decorative when used in purely visual contexts
 * - Preserves className for sizing/color via Tailwind
 */
export function DefaultNeedIcon({
  className = "w-20 h-20",
  id,
  isDecorative = false,
  title,
  type,
}: NeedIconProps) {
  const tagType = type?.toLowerCase() ?? "";
  const { IconComponent, iconTitle } = resolveIconAndTitle(tagType);
  const ariaProps = buildIconAriaProps(isDecorative, title ?? iconTitle);

  return (
    <IconComponent
      {...ariaProps}
      className={className}
      focusable={false}
      id={id}
    />
  );
}
